import { Table, Row, Col, Container, Button, Form } from "react-bootstrap";
import { LoadingSpinner, ModalMessage, PaginationMenu } from "../../components";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getStorageToken, useLogout } from "../../utilities";
import {
  createAttributeApi,
  deleteAttributeApi,
  fetchAllAttributesApi,
  updateAttributeApi,
} from "../../api/attributesApi";

export default function AdminAttributesList() {
  // displays a list of attributes
  // admin can add/edit/delete attributes

  const headers = ["Name", "Value"];
  const types = { DEFAULT: "", ADDING: "adding", EDITING: "editing" };
  const initialPaginationState = {
    pageNr: 1,
    totalNrOfPages: 1,
  };

  const logout = useLogout();
  const { stayLoggedIn } = useSelector((state) => state.user);

  const [attributes, setAttributes] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [addingEditing, setAddingEditing] = useState(types.DEFAULT); //"editing" / "adding"
  const [addEditAttr, setAddEditAttr] = useState({
    name: "",
    value: "",
    _id: "",
  });
  const [updateAttributesList, setUpdateAttributesList] = useState(true);
  const [paginationState, setPaginationState] = useState(
    {...initialPaginationState}
  );
  const [showAlertDelete, setShowAlertDelete] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchAttributes = async (pageNr = null) => {
      setIsLoading(true);
      try {
        const token = getStorageToken(stayLoggedIn);
        const res = await fetchAllAttributesApi(
          token,
          abortController.signal,
          pageNr
        );
        setUpdateAttributesList(false);
        return res;
      } catch (error) {
        setErrorMsg("Could not load attributes data. Please try again later.");
      } finally {
        setIsLoading(false);
        window.scrollTo(0, 0);
      }
    };

    if (updateAttributesList) {
      const pageNr = paginationState?.pageNr || 1;
      fetchAttributes(pageNr)
      .then((res) => {
        setAttributes(res?.attributes);
        setPaginationState({
          pageNr: res?.currPageNr || 1,
          totalNrOfPages: res?.totalNrOfPages || 1,
        });
      })
      .catch((error) =>
      setErrorMsg("Could not load data. Please try again later.")
      )
    }
    setUpdateAttributesList(false); 
    return () => {
      abortController.abort();
    };
  }, [updateAttributesList, paginationState.pageNr, stayLoggedIn]);


  const handleChangePageNr = (newPageNr) => {
    if (newPageNr < 1) {
      newPageNr = 1;
    } else if (newPageNr > paginationState.totalNrOfPages) {
      newPageNr = paginationState.totalNrOfPages;
    }
    setPaginationState({ ...paginationState, pageNr: newPageNr });
    setUpdateAttributesList(true);
  };

  const saveAttribute = async () => {
    try {
      const abortController = new AbortController();
      const token = getStorageToken(stayLoggedIn);
      const formattedAttributeName = addEditAttr.name + ":" + addEditAttr.value;
      let res;
      if (addingEditing === types.ADDING) {
        // creating a new attribute
        res = await createAttributeApi(
          token,
          formattedAttributeName,
          abortController.signal
        );
      } else if (addingEditing === types.EDITING) {
          res = await updateAttributeApi(
            token,
            addEditAttr._id,
            formattedAttributeName,
            abortController.signal
          );
      }
      if (res) {
        setSuccessMsg("Attribute saved successfully");
        setAddEditAttr({ name: "", value: "", _id: "" });
        setAddingEditing(types.DEFAULT);
        setUpdateAttributesList(true);
      }
    } catch (error) {
      if (error?.status === 401) {
        //unautorized, eg token expired
        logout();
      }
      setErrorMsg("Could not save the attribute. Please try again later.");
    }
  };

  const handleConfirmDelete = async () => {
    try {
        const token = getStorageToken(stayLoggedIn);
        const abortController = new AbortController();
        await deleteAttributeApi(token, showAlertDelete?.payload, abortController.signal);
        setSuccessMsg("Successfully deleted the attribute.");
        setUpdateAttributesList(true);
    } catch (error) {
      setErrorMsg("Could not delete attribute. Please try again later.");
    }
  };

  const handleEdit = (attr) => {
    setAddingEditing(types.EDITING);
    setAddEditAttr({
      name: attr.name.split(":")[0],
      value: attr.name.split(":")[1],
      _id: attr._id,
    });
  };

  const handleCancelClick = () => {
    setAddEditAttr({ name: "", value: "", _id: "" });
    setAddingEditing(types.DEFAULT);
  };

  const handleNameInput = (event) => {
    setAddEditAttr((prevAttr) => { return ({
      ...prevAttr, name: event.target.value });
    })
  };

  const handleValueInput = (event) => {
    setAddEditAttr((prevAttr) => { return (
      {...prevAttr, value: event.target.value }
    )})};

  return (
    <Container className="mt-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {successMsg && (
        <ModalMessage
          autoClose={true}
          type="success"
          message={successMsg}
          setSuccessMsg={setSuccessMsg}
        />
      )}
      <h1>Attributes List</h1>
      {isLoading && <LoadingSpinner />}
      {!attributes || attributes.length < 1 ? (
        <>{!isLoading && <p className="mt-2">No data available to show.</p>}</>
      ) : (
        <>
          <Button
            variant="primary"
            disabled={addingEditing}
            onClick={() => setAddingEditing(types.ADDING)}
          >
            Add Attribute
          </Button>

          {/* ADD/EDIT FORM */}
          {addingEditing && (
            <Form
              className="mt-4"
              style={{
                border: "#D3D3D3 2px solid",
                padding: "30px 0px",
                borderRadius: "10px",
              }}
            >
              <Row className="mx-3">
                <Col sm={6} className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder="Name"
                    value={addEditAttr.name}
                    onChange={handleNameInput}
                  />
                </Col>
                <Col sm={6} className="mb-2">
                  <Form.Control
                    type="text"
                    placeholder="Value"
                    value={addEditAttr.value}
                    onChange={handleValueInput}
                  />
                </Col>
              </Row>
              <Row className="ms-3">
                <Col>
                  <Button
                    variant="primary"
                    className="me-2"
                    onClick={async () => await saveAttribute()}
                  >
                    {addingEditing === types.ADDING ? "Add" : "Save"}
                  </Button>

                  <Button variant="danger" onClick={handleCancelClick}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          )}

          {/* ATTRIBUTES LIST */}
          <Table
            className="mt-4"
            rounded="md"
            hover
            responsive
            style={{ borderLeft: "none", borderRight: "none" }}
          >
            <thead>
              <tr>
                <th>#</th>
                {headers?.map((header, idx) => {
                  return <th key={idx}>{header}</th>;
                })}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {attributes?.map((attr, idx) => {
                return (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>{attr.name.split(":")[0]}</td>
                    <td>{attr.name.split(":")[1]}</td>
                    <td className="d-flex justify-content-end">
                      {/* show buttons */}
                      <Button
                        className="btn-sm"
                        variant="warning"
                        onClick={() => handleEdit(attr)}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </Button>
                      {"\u00A0\u00A0\u00A0"}
                      <Button
                        className="btn-sm "
                        variant="danger"
                        onClick={async () => setShowAlertDelete({show: true, payload: attr._id})}
                      >
                        <i className="bi bi-trash3-fill"></i>
                      </Button>{" "}
                      {"\u00A0\u00A0\u00A0"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </>
      )}

      {/* PAGINATION MENU */}
      {attributes && attributes.length > 0 && (
        <PaginationMenu
          pageNr={paginationState.pageNr}
          totalNrOfPages={paginationState.totalNrOfPages}
          handleChangePageNr={handleChangePageNr}
        />
      )}
          {/* alert message */}
          {showAlertDelete?.show && 
      <ModalMessage
          message= "Are you sure you want to delete this attribute? This will remove the attribute from any associated products as well."
          // setErrorMsg={setShowLogoutMsg}
          confirmButton={true}
          handleConfirm={handleConfirmDelete}
          setShowAlertMsg={setShowAlertDelete}
        />
      }
    </Container>
  );
}